import type { TimeClockPolicy } from '@/store/useTimeClockPolicyStore';

/** Limits mirror the backend validation behind `planning.timeClock.invalidPolicy`. */
export const POLICY_LIMITS = {
  maxTiers: 5,
  tierThresholdMin: 1,
  tierThresholdMax: 1440,
  tierBreakMin: 1,
  tierBreakMax: 240,
  minBreakSegmentMin: 0,
  minBreakSegmentMax: 60,
  maxWorkMinutesMin: 60,
  maxWorkMinutesMax: 1440,
} as const;

export const POLICY_TIME_ZONES = [
  'Europe/Vienna',
  'Europe/Berlin',
  'Europe/Ljubljana',
  'Europe/Zurich',
  'Europe/Rome',
];

export interface BreakTierForm {
  /** Stable React key — not sent to the server and ignored by the dirty check. */
  key: number;
  hours: string;
  minutes: string;
  breakMinutes: string;
}

export interface PolicyForm {
  isEnabled: boolean;
  tiers: BreakTierForm[];
  minBreakSegmentMinutes: string;
  maxWorkHours: string;
  stampingStartHour: string;
  stampingEndHour: string;
  workCountStartHour: string;
  timeZoneId: string;
}

export interface TierErrors {
  threshold?: string;
  breakMinutes?: string;
}

export interface PolicyErrors {
  tiers?: string;
  tierErrors?: Record<number, TierErrors>;
  minBreakSegmentMinutes?: string;
  maxWorkHours?: string;
  stampingWindow?: string;
  workCountStartHour?: string;
  timeZoneId?: string;
}

let nextTierKey = 1;

export function newTier(workAfterMinutes: number | null, breakMinutes: number | null): BreakTierForm {
  return {
    key: nextTierKey++,
    hours: workAfterMinutes == null ? '' : String(Math.floor(workAfterMinutes / 60)),
    minutes: workAfterMinutes == null ? '' : String(workAfterMinutes % 60).padStart(2, '0'),
    breakMinutes: breakMinutes == null ? '' : String(breakMinutes),
  };
}

function minutesToHours(minutes: number): string {
  return String(Math.round((minutes / 60) * 100) / 100);
}

export function policyToForm(policy: TimeClockPolicy): PolicyForm {
  return {
    isEnabled: policy.isEnabled ?? true,
    tiers: (policy.breakTiers ?? []).map((tier) =>
      newTier(tier.workAfterMinutes ?? 0, tier.breakMinutes ?? 0),
    ),
    minBreakSegmentMinutes: String(policy.minBreakSegmentMinutes ?? 0),
    maxWorkHours: minutesToHours(policy.maxWorkMinutesPerDay ?? 600),
    stampingStartHour: String(policy.stampingStartHour ?? 5),
    stampingEndHour: String(policy.stampingEndHour ?? 20),
    workCountStartHour: String(policy.workCountStartHour ?? 6),
    timeZoneId: policy.timeZoneId ?? '',
  };
}

/**
 * § 11 AZG: more than 6 h → 30 min, segments of at least 10 min; 10 h max, window 5–20,
 * work counts from 06:00.
 */
export function austrianDefaultForm(): PolicyForm {
  return {
    isEnabled: true,
    tiers: [newTier(360, 30)],
    minBreakSegmentMinutes: '10',
    maxWorkHours: '10',
    stampingStartHour: '5',
    stampingEndHour: '20',
    workCountStartHour: '6',
    timeZoneId: 'Europe/Vienna',
  };
}

function tierThreshold(tier: BreakTierForm): number {
  return Number(tier.hours || '0') * 60 + Number(tier.minutes || '0');
}

export function formToPolicy(form: PolicyForm): TimeClockPolicy {
  return {
    isEnabled: form.isEnabled,
    breakTiers: form.tiers.map((tier) => ({
      workAfterMinutes: tierThreshold(tier),
      breakMinutes: Number(tier.breakMinutes),
    })),
    minBreakSegmentMinutes: Number(form.minBreakSegmentMinutes),
    maxWorkMinutesPerDay: Math.round(Number(form.maxWorkHours) * 60),
    stampingStartHour: Number(form.stampingStartHour),
    stampingEndHour: Number(form.stampingEndHour),
    workCountStartHour: Number(form.workCountStartHour),
    timeZoneId: form.timeZoneId,
  };
}

export function policyFormsEqual(a: PolicyForm, b: PolicyForm): boolean {
  const strip = (form: PolicyForm) =>
    JSON.stringify({
      ...form,
      tiers: form.tiers.map(({ hours, minutes, breakMinutes }) => [hours, minutes, breakMinutes]),
    });
  return strip(a) === strip(b);
}

/**
 * Backend rule for a clock-in window: `0 ≤ start ≤ countStart < end ≤ 24`, whole local hours.
 * Shared by the tenant policy and the time clock groups.
 */
export function isValidStampingRule(start: number, end: number, countStart: number): boolean {
  return (
    [start, end, countStart].every(Number.isInteger) &&
    start >= 0 &&
    start <= countStart &&
    countStart < end &&
    end <= 24
  );
}

function isIntegerIn(raw: string, min: number, max: number): boolean {
  if (raw.trim() === '') return false;
  const value = Number(raw);
  return Number.isInteger(value) && value >= min && value <= max;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function validateTiers(tiers: BreakTierForm[], t: Translate, errors: PolicyErrors) {
  const L = POLICY_LIMITS;
  if (tiers.length > L.maxTiers) {
    errors.tiers = t('validation.tooManyTiers', { max: L.maxTiers });
  }

  const tierErrors: Record<number, TierErrors> = {};
  const addError = (key: number, field: keyof TierErrors, message: string) => {
    tierErrors[key] = { ...tierErrors[key], [field]: tierErrors[key]?.[field] ?? message };
  };

  const valid: { key: number; threshold: number; breakMinutes: number }[] = [];
  const seen = new Set<number>();
  for (const tier of tiers) {
    const threshold = tierThreshold(tier);
    const thresholdOk =
      (tier.hours.trim() !== '' || tier.minutes.trim() !== '') &&
      (tier.hours.trim() === '' || isIntegerIn(tier.hours, 0, 24)) &&
      (tier.minutes.trim() === '' || isIntegerIn(tier.minutes, 0, 59)) &&
      threshold >= L.tierThresholdMin &&
      threshold <= L.tierThresholdMax;
    if (!thresholdOk) {
      addError(tier.key, 'threshold', t('validation.tierThresholdRange'));
    } else if (seen.has(threshold)) {
      addError(tier.key, 'threshold', t('validation.tierThresholdDuplicate'));
    }
    seen.add(threshold);

    const breakOk = isIntegerIn(tier.breakMinutes, L.tierBreakMin, L.tierBreakMax);
    if (!breakOk) addError(tier.key, 'breakMinutes', t('validation.tierBreakRange'));
    if (thresholdOk && breakOk) {
      valid.push({ key: tier.key, threshold, breakMinutes: Number(tier.breakMinutes) });
    }
  }

  valid.sort((a, b) => a.threshold - b.threshold);
  for (let i = 1; i < valid.length; i++) {
    const [previous, current] = [valid[i - 1], valid[i]];
    if (previous.threshold !== current.threshold && current.breakMinutes <= previous.breakMinutes) {
      addError(current.key, 'breakMinutes', t('validation.tierBreakOrder'));
    }
  }

  if (Object.keys(tierErrors).length > 0) errors.tierErrors = tierErrors;
}

/**
 * Client-side mirror of the backend rules. `t` must resolve keys below
 * `tenantSettings:timeClockPolicy`.
 */
export function validatePolicyForm(form: PolicyForm, t: Translate): PolicyErrors {
  const L = POLICY_LIMITS;
  const errors: PolicyErrors = {};
  validateTiers(form.tiers, t, errors);

  if (!isIntegerIn(form.minBreakSegmentMinutes, L.minBreakSegmentMin, L.minBreakSegmentMax)) {
    errors.minBreakSegmentMinutes = t('validation.minBreakSegmentRange');
  }

  const maxWorkMinutes = Math.round(Number(form.maxWorkHours) * 60);
  if (
    form.maxWorkHours.trim() === '' ||
    !Number.isFinite(maxWorkMinutes) ||
    maxWorkMinutes < L.maxWorkMinutesMin ||
    maxWorkMinutes > L.maxWorkMinutesMax
  ) {
    errors.maxWorkHours = t('validation.maxWorkRange');
  }

  const start = Number(form.stampingStartHour);
  const end = Number(form.stampingEndHour);
  if (!(start >= 0 && start < end && end <= 24)) {
    errors.stampingWindow = t('validation.windowOrder');
  } else if (!isValidStampingRule(start, end, Number(form.workCountStartHour))) {
    errors.workCountStartHour = t('validation.workCountStartRange');
  }

  if (form.timeZoneId.trim() === '') {
    errors.timeZoneId = t('validation.timeZoneRequired');
  }

  return errors;
}

export function hasPolicyErrors(errors: PolicyErrors): boolean {
  return Object.keys(errors).length > 0;
}
